import React from "react";

interface ShipTooltipProps {
  visible: boolean;
  x: number;
  y: number;
  shipData: {
    id: string;
    name: string;
    ownerName: string;
    ownerType: "player" | "npc";
    ownerId: string;
    hullHp: number;
    maxHullHp: number;
    level?: number;
    faction?: string;
    subfaction?: string;
    bounty?: number;
    isAggroed?: boolean;
    accountUsername?: string;
    accountStatus?: string;
    fleetName?: string;
    shipColor?: string;
  };
  localPlayerId: string;
  localShipPosition?: { x: number; y: number };
  shipPosition: { x: number; y: number };
  portraitIndex?: number;
}

export const ShipTooltip: React.FC<ShipTooltipProps> = ({
  visible,
  x,
  y,
  shipData,
  localPlayerId,
  localShipPosition,
  shipPosition,
  portraitIndex,
}) => {
  if (!visible) return null;

  const isNPC = shipData.ownerType === "npc";
  const isLocal = shipData.ownerId === localPlayerId;
  const hasBounty = isNPC && shipData.bounty && shipData.bounty > 0;
  const portraitSize = 48;
  const tooltipWidth = 180;
  const tooltipHeight = isNPC ? (hasBounty ? 130 : 115) : 95;

  const getBorderColor = (): string => {
    if (isNPC) {
      const factionColors: Record<string, string> = {
        navy: "#3498db",
        pirate: "#9b59b6",
        bandit: "#e74c3c",
        merchant: "#f39c12",
        corsairs: "#dc143c",
        stormbreaker: "#1e3a8a",
        reavers: "#1f2937",
        smuggler: "#8e44ad",
      };
      const displayFaction = shipData.subfaction || shipData.faction;
      return factionColors[displayFaction || ""] || "#888888";
    }
    // For players, use shipColor if available, otherwise use default
    if (shipData.shipColor) {
      return shipData.shipColor;
    }
    return "#3498db";
  };

  const getPlayerNameColor = (): string => {
    if (shipData.accountStatus === "admin") return "#e74c3c";
    if (shipData.accountStatus === "moderator") return "#3498db";
    if (shipData.accountStatus === "supporter") return "#f39c12";
    return "#ffffff";
  };

  const buildPlayerDisplayName = (): string => {
    if (shipData.fleetName) {
      return `[${shipData.fleetName}] ${shipData.name}`;
    }
    return shipData.name;
  };

  const baseDisplayName = isNPC ? shipData.ownerName : buildPlayerDisplayName();
  // For players, don't add "(You)" - remove that distinction
  const displayName = baseDisplayName;

  const baseNameColor = isNPC ? "#f39c12" : getPlayerNameColor();
  const nameColor =
    isLocal && (!shipData.accountStatus || shipData.accountStatus === "user")
      ? "#2ecc71"
      : baseNameColor;

  const hpPercent = shipData.hullHp / shipData.maxHullHp;
  const hpColor =
    hpPercent > 0.5 ? "#2ecc71" : hpPercent > 0.25 ? "#f39c12" : "#e74c3c";

  const distance = localShipPosition
    ? Math.round(
        Math.sqrt(
          Math.pow(localShipPosition.x - shipPosition.x, 2) +
            Math.pow(localShipPosition.y - shipPosition.y, 2),
        ) / 32,
      )
    : null;
  const inRange = distance !== null && distance <= 8;

  const formatBounty = (bounty: number): string => {
    if (bounty >= 1000000) {
      return `${(bounty / 1000000).toFixed(1)}M`;
    } else if (bounty >= 1000) {
      return `${(bounty / 1000).toFixed(1)}K`;
    }
    return bounty.toString();
  };

  const factionLabels: Record<string, string> = {
    navy: "NAVY",
    pirate: "PIRATE",
    bandit: "BANDIT",
    merchant: "MERCHANT",
    corsairs: "CRIMSON CORSAIRS",
    stormbreaker: "STORMBREAKER SYNDICATE",
    reavers: "OBSIDIAN REAVERS",
    smuggler: "SMUGGLERS NETWORK",
  };

  const factionColors: Record<string, string> = {
    navy: "#3498db",
    pirate: "#9b59b6",
    bandit: "#e74c3c",
    merchant: "#f39c12",
    corsairs: "#dc143c",
    stormbreaker: "#1e3a8a",
    reavers: "#1f2937",
    smuggler: "#8e44ad",
  };

  const getPortraitPath = (): string | null => {
    if (isNPC && portraitIndex !== undefined) {
      const faction = shipData.subfaction || shipData.faction || "neutral";
      // Map factions to portrait folders
      if (faction === "navy") {
        return `/assets/portraits/marines/marine_${(portraitIndex % 5) + 1}.png`;
      } else if (
        faction === "pirate" ||
        faction === "corsairs" ||
        faction === "reavers"
      ) {
        return `/assets/portraits/pirates/pirate_${(portraitIndex % 16) + 1}.png`;
      } else if (faction === "bandit" || faction === "smuggler") {
        return `/assets/portraits/pirates/pirate_${(portraitIndex % 16) + 1}.png`;
      } else if (faction === "merchant" || faction === "stormbreaker") {
        return `/assets/portraits/pirates/pirate_${(portraitIndex % 16) + 1}.png`;
      }
    }
    return null;
  };

  const getPortraitLabel = (): string => {
    if (isNPC) {
      const faction = shipData.faction || "neutral";
      return faction.charAt(0).toUpperCase();
    }
    return "⚓";
  };

  const portraitPath = getPortraitPath();
  const borderColor = getBorderColor();

  return (
    <div
      style={{
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
        width: `${tooltipWidth}px`,
        height: `${tooltipHeight}px`,
        backgroundColor: "#000000",
        border: `2px solid ${borderColor}`,
        borderRadius: "0px",
        zIndex: 10000,
        fontFamily: '"Sometype Mono", monospace',
        boxSizing: "border-box",
      }}
    >
      {/* Portrait - only show for NPCs */}
      {isNPC && (
        <div
          style={{
            position: "absolute",
            left: "6px",
            top: "6px",
            width: `${portraitSize}px`,
            height: `${portraitSize}px`,
            backgroundColor: "#1a1a1a",
            border: `2px solid ${borderColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {portraitPath ? (
            <img
              src={portraitPath}
              alt="Portrait"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                padding: "2px",
                boxSizing: "border-box",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget
                  .nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : null}
          <div
            style={{
              display: portraitPath ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {getPortraitLabel()}
          </div>
        </div>
      )}

      {/* Name */}
      <div
        style={{
          position: "absolute",
          left: isNPC ? `${6 + portraitSize + 8}px` : "10px",
          top: "10px",
          right: "10px",
          fontSize: "11px",
          fontWeight: "bold",
          color: nameColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayName}
      </div>

      {/* Account Username */}
      {!isNPC && shipData.accountUsername && (
        <div
          style={{
            position: "absolute",
            left: "10px",
            top: "26px",
            fontSize: "9px",
            color: "#8b949e",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          @{shipData.accountUsername}
        </div>
      )}

      {/* Fleet Name */}
      {!isNPC && shipData.fleetName && (
        <div
          style={{
            position: "absolute",
            left: "10px",
            top: shipData.accountUsername ? "40px" : "26px",
            fontSize: "9px",
            color: "#a371f7",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {shipData.fleetName}
        </div>
      )}

      {/* Faction Label */}
      {isNPC && shipData.faction && (
        <div
          style={{
            position: "absolute",
            left: `${6 + portraitSize + 8}px`,
            top: "26px",
            fontSize: "9px",
            color:
              factionColors[shipData.subfaction || shipData.faction || ""] ||
              "#888888",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {factionLabels[shipData.subfaction || shipData.faction || ""] ||
            (shipData.subfaction || shipData.faction || "").toUpperCase()}
        </div>
      )}

      {/* HP Bar Background */}
      <div
        style={{
          position: "absolute",
          left: "8px",
          top: isNPC ? `${6 + portraitSize + 6}px` : "42px",
          width: `${tooltipWidth - 16}px`,
          height: "8px",
          backgroundColor: "#333333",
        }}
      >
        {/* HP Bar Fill */}
        <div
          style={{
            height: "100%",
            width: `${hpPercent * 100}%`,
            backgroundColor: hpColor,
          }}
        />
      </div>

      {/* HP Text */}
      <div
        style={{
          position: "absolute",
          left: "8px",
          top: isNPC ? `${6 + portraitSize + 6 + 12}px` : "54px",
          fontSize: "9px",
          color: "#ffffff",
        }}
      >
        HP: {shipData.hullHp}/{shipData.maxHullHp}
      </div>

      {/* Distance */}
      {distance !== null && !isLocal && (
        <div
          style={{
            position: "absolute",
            left: "8px",
            top: isNPC ? `${6 + portraitSize + 6 + 26}px` : "68px",
            fontSize: "9px",
            color: inRange ? "#2ecc71" : "#666666",
          }}
        >
          DIST: {distance}T
        </div>
      )}

      {/* Bounty */}
      {hasBounty && shipData.bounty && (
        <div
          style={{
            position: "absolute",
            left: "8px",
            top: `${6 + portraitSize + 6 + (localShipPosition && !isLocal ? 40 : 26)}px`,
            fontSize: "9px",
            color: "#f39c12",
            fontWeight: "bold",
          }}
        >
          {formatBounty(shipData.bounty)}
        </div>
      )}
    </div>
  );
};
